import type { ReactNode } from 'react';
import '@/styles/future-icons.css';

/**
 * AQUAMARINA's own line-icon set: thin neon strokes on a 24px grid, each with one moving part
 * (a spinning wheel, a rolling wave, a rising sun…) that animates on hover and on the active state.
 * Strokes "draw in" once when an icon mounts. Colour comes from `currentColor`.
 */
export type FutureIconName =
  | 'bag'
  | 'compass'
  | 'schooner'
  | 'speedboat'
  | 'shared'
  | 'jeep'
  | 'mountains'
  | 'calendar'
  | 'menu'
  | 'close';

const ICONS: Record<FutureIconName, ReactNode> = {
  bag: (
    <>
      <path className="fi-draw fi-handle" d="M8.5 9V7.2a3.5 3.5 0 0 1 7 0V9" />
      <path className="fi-draw" d="M5.2 9h13.6l-1 10.1a2 2 0 0 1-2 1.9H8.2a2 2 0 0 1-2-1.9z" />
      <path className="fi-glow fi-spark" d="M12 12.6v3.8M10.1 14.5h3.8" />
    </>
  ),
  compass: (
    <>
      <circle className="fi-draw" cx="12" cy="12" r="8.6" />
      <path className="fi-soft" d="M12 2.6v1.8M12 19.6v1.8M2.6 12h1.8M19.6 12h1.8" />
      <g className="fi-needle">
        <path className="fi-glow" d="m12 6.6 2 5.4-2 5.4-2-5.4z" />
        <circle cx="12" cy="12" r="0.9" className="fi-fill" />
      </g>
    </>
  ),
  schooner: (
    <>
      <path className="fi-draw" d="M3.5 15.5h17l-2.3 3.2H5.8z" />
      <path className="fi-draw" d="M9 15.5V4.5M15 15.5V6.8" />
      <path className="fi-glow fi-sail" d="M9 5.2 4.6 13.4H9M15 7.6l3.8 5.8H15" />
      <path className="fi-wave" d="M1.5 21.2c1.4 0 1.4-1 2.8-1s1.4 1 2.8 1 1.4-1 2.8-1 1.4 1 2.8 1 1.4-1 2.8-1 1.4 1 2.8 1 1.4-1 2.8-1 1.4 1 2.8 1" />
    </>
  ),
  speedboat: (
    <>
      <path className="fi-draw" d="M3 14.2h15.6a2.6 2.6 0 0 1-2.4 3.6H6.4z" />
      <path className="fi-draw" d="M8.2 14.2 10.6 10h3.6l1.4 4.2" />
      <path className="fi-glow fi-speed" d="M1.6 9.6h3.6M2.6 11.8h2.6M21 12.4h1.6" />
      <path className="fi-wave" d="M1.5 21c1.4 0 1.4-1 2.8-1s1.4 1 2.8 1 1.4-1 2.8-1 1.4 1 2.8 1 1.4-1 2.8-1 1.4 1 2.8 1 1.4-1 2.8-1 1.4 1 2.8 1" />
    </>
  ),
  shared: (
    <>
      <path className="fi-draw" d="M3 14.4h18l-2.4 3.6H5.4z" />
      <g className="fi-crew">
        <circle className="fi-glow" cx="7.6" cy="8.4" r="1.6" />
        <circle className="fi-glow" cx="12" cy="7.2" r="1.8" />
        <circle className="fi-glow" cx="16.4" cy="8.4" r="1.6" />
      </g>
      <path className="fi-soft" d="M5.6 14.4a2 2 0 0 1 4 0M9.8 14.4a2.2 2.2 0 0 1 4.4 0M14.4 14.4a2 2 0 0 1 4 0" />
      <path className="fi-wave" d="M1.5 21.2c1.4 0 1.4-1 2.8-1s1.4 1 2.8 1 1.4-1 2.8-1 1.4 1 2.8 1 1.4-1 2.8-1 1.4 1 2.8 1 1.4-1 2.8-1 1.4 1 2.8 1" />
    </>
  ),
  jeep: (
    <>
      <path className="fi-draw" d="M2.8 15.6V11.4l2-.6 1.8-4h7.6l1.4 4h4.6a1.4 1.4 0 0 1 1.4 1.4v3.4h-1.8M9.6 15.6h4.6M2.8 15.6h1.6" />
      <path className="fi-soft" d="M7.6 10.8h5.8M10.6 7v3.8" />
      <g className="fi-wheel" style={{ transformOrigin: '7px 16px' }}>
        <circle className="fi-glow" cx="7" cy="16" r="2.4" />
        <path className="fi-soft" d="M7 13.6v4.8M4.6 16h4.8" />
      </g>
      <g className="fi-wheel" style={{ transformOrigin: '16.8px 16px' }}>
        <circle className="fi-glow" cx="16.8" cy="16" r="2.4" />
        <path className="fi-soft" d="M16.8 13.6v4.8M14.4 16h4.8" />
      </g>
      <path className="fi-trail" d="M1.4 20.4h3M8 20.4h6M17.4 20.4h5" />
    </>
  ),
  mountains: (
    <>
      <circle className="fi-glow fi-sun" cx="17.4" cy="6.4" r="2.2" />
      <path className="fi-draw" d="m2.4 19.4 6.2-10.6 4 6.8M10.4 19.4l5-7.6 6.2 7.6z" />
      <path className="fi-soft" d="m6.9 11.7 1.7 1 1.6-1" />
      <path className="fi-draw" d="M2.4 19.4h19.2" />
    </>
  ),
  calendar: (
    <>
      <path className="fi-draw" d="M6.6 5.2h10.8a3 3 0 0 1 3 3v9a3 3 0 0 1-3 3H6.6a3 3 0 0 1-3-3v-9a3 3 0 0 1 3-3z" />
      <path className="fi-soft" d="M3.6 9.8h16.8M8.2 3.2v3.6M15.8 3.2v3.6" />
      <path className="fi-glow fi-check" d="m9 15 2.2 2.2 4-4.2" pathLength={1} />
    </>
  ),
  menu: (
    <>
      <path className="fi-draw fi-bar1" d="M4 8.5h16" />
      <path className="fi-glow fi-bar2" d="M9 15.5h11" />
    </>
  ),
  close: (
    <>
      <path className="fi-draw" d="m6.5 6.5 11 11" />
      <path className="fi-glow" d="m17.5 6.5-11 11" />
    </>
  ),
};

interface FutureIconProps {
  name: FutureIconName;
  className?: string;
  /** Rendered size in px (defaults to 1.25em of the surrounding text). */
  size?: number;
}

export default function FutureIcon({ name, className = '', size }: FutureIconProps) {
  return (
    <svg
      className={`fi fi--${name} ${className}`}
      viewBox="0 0 24 24"
      width={size ?? '1.25em'}
      height={size ?? '1.25em'}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {ICONS[name]}
    </svg>
  );
}
